import { fixedUi } from '@/i18n/fixedUi';
/**
 * TEMU 店铺基本信息解析
 * 来源：bg.mall.info.get（CN）+ bg.open.accesstoken.info.get.global（PA）缓存；
 * 店铺数字 ID（商家 ID）另由尺码表响应的 result.sizeSpecDataList[].supplierId 兜底写入
 * （见 TemuShopProfileService.probeMallIdFromSizeCharts）。
 * CN 旧 type bg.open.accesstoken.info.get 已下线（打 CN/PA 均 7000016），不再同步，也不返回 mallId。
 */

const AUTH_STATUS_LABELS = {
  CONNECTED: '已授权',
  connected: '已授权',
  EXPIRED: '授权过期',
  expired: '授权过期',
  PENDING: '待授权',
  pending: '待授权',
};

const FIELD_LABELS = {
  mallName: '店铺名称',
  storeName: '店铺名称',
  // Temu 的店铺数字 ID（mallId / supplierId）实质是商家 ID，界面统一叫「商家 ID」
  mallId: '商家 ID',
  supplierId: '商家 ID',
  uniqueId: '店铺唯一标识',
  shopTrackLabel: '店铺类型',
  shopType: '店铺类型代码',
  semiManagedMall: '是否半托管',
  isThriftStore: '是否二手店',
  temuRegion: '区域',
  authStatus: '授权状态',
  expiredTime: '授权过期时间',
};

/** 基本信息展示字段（隐藏 uniqueId / semiManagedMall / isThriftStore / temuRegion） */
const STORE_INFO_FIELD_ORDER = [
  'mallName',
  'mallId',
  'shopTrackLabel',
  'authStatus',
  'expiredTime',
];

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** 兼容后端已 unwrap 的 info，以及仍带 info 外层的原始响应 */
export function unwrapStoreInfoPayload(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.info && isPlainObject(raw.info)) return raw.info;
  return raw;
}

function formatExpiredTime(value) {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num) || num <= 0) return '—';
  // Temu 返回秒级时间戳
  const ms = num > 1e12 ? num : num * 1000;
  try {
    return new Date(ms).toLocaleString('zh-CN');
  } catch {
    return String(value);
  }
}

function isExpiredEpoch(value) {
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num) || num <= 0) return false;
  const ms = num > 1e12 ? num : num * 1000;
  return ms <= Date.now();
}

export function formatStoreInfoValue(key, value) {
  if (value === undefined || value === null || value === '') return '—';

  if (key === 'authStatus') {
    return AUTH_STATUS_LABELS[value] ?? AUTH_STATUS_LABELS[String(value)] ?? String(value);
  }

  if (key === 'semiManagedMall' || key === 'isThriftStore') {
    if (typeof value === 'boolean') return value ? '是' : '否';
    if (value === 1 || value === '1' || value === 'true') return '是';
    if (value === 0 || value === '0' || value === 'false') return '否';
  }

  if (key === 'expiredTime') {
    return formatExpiredTime(value);
  }

  if (key === 'mallId' || key === 'supplierId') {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return value ? fixedUi("是") : fixedUi("否");
  }

  return String(value);
}

function pickSectionRows(source, fieldOrder) {
  return fieldOrder.map((key) => ({
    key,
    label: FIELD_LABELS[key] || key,
    value: formatStoreInfoValue(key, isPlainObject(source) ? source[key] : undefined),
  }));
}

function readNested(payload, key) {
  if (!payload) return null;
  if (isPlainObject(payload[key])) return payload[key];
  return null;
}

/** 取出店铺基本信息对象（Temu mall/token 字段，或兼容 Shein storeInfo） */
export function pickStoreInfoNode(raw) {
  const payload = unwrapStoreInfoPayload(raw);
  if (!payload) return null;
  const nested = readNested(payload, 'storeInfo');
  if (nested) return nested;
  if (
    Object.prototype.hasOwnProperty.call(payload, 'mallName')
    || Object.prototype.hasOwnProperty.call(payload, 'storeName')
    || Object.prototype.hasOwnProperty.call(payload, 'mallId')
    || Object.prototype.hasOwnProperty.call(payload, 'supplierId')
    || Object.prototype.hasOwnProperty.call(payload, 'semiManagedMall')
    || Object.prototype.hasOwnProperty.call(payload, 'authStatus')
  ) {
    return payload;
  }
  return null;
}

/** Temu 无发品额度；保留空实现以免旧调用报错 */
export function pickStoreQuotaNode(_raw) {
  return null;
}

export function getStoreStatusMeta(raw) {
  const node = pickStoreInfoNode(raw);
  if (node && isExpiredEpoch(node.expiredTime)) {
    return { code: 'expired', label: '授权过期', modifier: 'failed' };
  }
  const auth = node?.authStatus;
  if (auth === undefined || auth === null || auth === '') {
    const code = node?.storeStatus;
    if (code === 1 || code === '1') return { code, label: '已授权', modifier: 'success' };
    if (code === 0 || code === '0') return { code, label: '未授权', modifier: 'failed' };
    return { code: null, label: '—', modifier: 'offline' };
  }
  const label = formatStoreInfoValue('authStatus', auth);
  const key = String(auth).toUpperCase();
  if (key === 'CONNECTED') return { code: auth, label, modifier: 'success' };
  if (key === 'EXPIRED') return { code: auth, label, modifier: 'failed' };
  return { code: auth, label, modifier: 'pending' };
}

export function getStoreDisplayName(raw, fallback = '') {
  const node = pickStoreInfoNode(raw);
  const name = node?.mallName || node?.storeName;
  if (name !== undefined && name !== null && String(name).trim() !== '') {
    return String(name).trim();
  }
  return fallback || '—';
}

export function getStoreSupplierId(raw) {
  const node = pickStoreInfoNode(raw);
  const id = node?.mallId ?? node?.supplierId;
  if (id === undefined || id === null || id === '') return '';
  return String(id);
}

export function buildStoreBasicRows(raw) {
  const node = pickStoreInfoNode(raw);
  if (!node) return pickSectionRows(null, STORE_INFO_FIELD_ORDER);
  // mallName 优先；若只有 storeName 则映射一下展示
  const source = { ...node };
  if (!source.mallName && source.storeName) source.mallName = source.storeName;
  if (!source.mallId && source.supplierId) source.mallId = source.supplierId;
  if (isExpiredEpoch(source.expiredTime)) {
    source.authStatus = 'expired';
  }
  return pickSectionRows(source, STORE_INFO_FIELD_ORDER);
}

export function buildStoreQuotaRows(_raw) {
  return [];
}

export function buildStoreInfoSections(raw) {
  const payload = unwrapStoreInfoPayload(raw);
  if (!payload) return [];

  const sections = [];
  const basicRows = buildStoreBasicRows(payload);
  if (basicRows.some((row) => row.value !== '—') || pickStoreInfoNode(payload)) {
    sections.push({ id: 'storeInfo', title: '基本信息', rows: basicRows });
  }
  return sections;
}
